import { useState, useEffect } from 'react'
import { supabase } from '@/integrations/supabase/client'
import { IS_DEMO_MODE } from '@/config/config'
import { PageHeader } from '@/components/ui/PageHeader'
import { Copy, Pencil, Check, X, User, UsersRound } from 'lucide-react'
import { useAppData } from '@/contexts/AppDataContext'
import { useAuth } from '@/contexts/AuthContext'
import { computeNewTags } from '@/components/ui/TagBadges'
import { SmartCombobox, invalidateDistinctCache } from '@/components/ui/SmartCombobox'
import {
    SEXE, GENRE, POSSESSION_CI, TEL_QUI, STATUT_ACCOMP, REFERENTE, SITUATION_RESID, BAILLEUR,
    CAUSE_SUSP, ORGANISME_PAIEMENT, OUI_NON, OUI_NON_EN_COURS, OK_PAS_OK, ADMIN_TYPE,
    LIEU_VIE_TYPE, LOGEMENT_TYPE, GL_TYPE, GL_REMBOURSEMENT, RAISON_RENOM
} from '@/config/enums'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type FullBeneficiary = Record<string, any>

type FieldType = 'text' | 'date' | 'number' | 'enum' | 'combobox' | 'textarea' | 'readonly' | 'boolean' | 'worker'

interface FieldConfig {
    key: string
    label: string
    type: FieldType
    options?: string[]
    required?: boolean
}

interface SectionConfig {
    title: string
    fields: FieldConfig[]
    /** Tailwind classes for the section header bg + text */
    headerBg: string
    headerText: string
    /** Tailwind border class for the card itself */
    cardBorder: string
}

// enums imported from @/config/enums

// ── Computed helpers ───────────────────────────────────────────────────────
const computeMonthsSince = (dateStr: string | null | undefined): number | null => {
    if (!dateStr) return null
    const d = new Date(dateStr)
    if (isNaN(d.getTime())) return null
    const now = new Date()
    return Math.floor((now.getFullYear() - d.getFullYear()) * 12 + (now.getMonth() - d.getMonth()))
}

const computeAge = (dateStr: string | null | undefined): number | null => {
    if (!dateStr) return null
    const birth = new Date(dateStr)
    if (isNaN(birth.getTime())) return null
    const now = new Date()
    let age = now.getFullYear() - birth.getFullYear()
    const m = now.getMonth() - birth.getMonth()
    if (m < 0 || (m === 0 && now.getDate() < birth.getDate())) age--
    return age
}

const convColor = (months: number | null) => {
    if (months === null) return null
    return months >= 12 ? 'red' : months >= 9 ? 'orange' : 'green'
}

// ── Section configuration ──────────────────────────────────────────────────
const SECTIONS: SectionConfig[] = [
    {
        title: 'Généralités',
        headerBg: 'bg-orange-50',
        headerText: 'text-orange-800',
        cardBorder: 'border-orange-200',
        fields: [
            { key: 'nom', label: 'Nom', type: 'text', required: true },
            { key: 'prenom', label: 'Prénom', type: 'text', required: true },
            { key: 'nationalite', label: 'Nationalité', type: 'text' },
            { key: 'sexe', label: 'Sexe', type: 'enum', options: SEXE },
            { key: 'genre', label: 'Genre', type: 'enum', options: GENRE },
            { key: 'date_naissance', label: 'Date de naissance', type: 'date', required: true },
            { key: 'age', label: 'Âge', type: 'number' },
            { key: 'lieu_naissance', label: 'Lieu de naissance', type: 'text' },
            { key: 'etat_civil', label: 'État civil', type: 'text' },
        ],
    },
    {
        title: "Carte d'identité",
        headerBg: 'bg-blue-50',
        headerText: 'text-blue-800',
        cardBorder: 'border-blue-200',
        fields: [
            { key: 'registre_national', label: 'N° de Registre national', type: 'text', required: true },
            { key: 'type_carte_identite', label: "Type de carte d'identité", type: 'text' },
            { key: 'numero_carte_identite', label: "N° de carte d'identité", type: 'text' },
            { key: 'code_pin_carte_identite', label: 'Code PIN', type: 'text' },
            { key: 'date_fin_validite_carte_identite', label: 'Date de fin de validité', type: 'date' },
            { key: 'personne_possession_ci', label: 'Personne en possession de la CI', type: 'enum', options: POSSESSION_CI },
            { key: 'adresse_reference', label: 'Adresse de référence', type: 'text' },
        ],
    },
    {
        title: 'Informations de contact',
        headerBg: 'bg-green-50',
        headerText: 'text-green-800',
        cardBorder: 'border-green-200',
        fields: [
            { key: 'telephone_1', label: 'N° de téléphone 1', type: 'text' },
            { key: 'telephone_1_qui', label: 'Tél 1 - Qui ?', type: 'enum', options: TEL_QUI },
            { key: 'telephone_2', label: 'N° de téléphone 2', type: 'text' },
            { key: 'telephone_2_qui', label: 'Tél 2 - Qui ?', type: 'enum', options: TEL_QUI },
            { key: 'telephone_3', label: 'N° de téléphone 3', type: 'text' },
            { key: 'telephone_3_qui', label: 'Tél 3 - Qui ?', type: 'enum', options: TEL_QUI },
            { key: 'email', label: 'Adresse e-mail', type: 'text' },
        ],
    },
    {
        title: 'Accompagnement Housing First',
        headerBg: 'bg-yellow-50',
        headerText: 'text-yellow-800',
        cardBorder: 'border-yellow-200',
        fields: [
            { key: 'statut_accompagnement', label: "Statut de l'accompagnement", type: 'enum', options: STATUT_ACCOMP, required: true },
            { key: 'referente', label: 'Référent.e', type: 'worker', required: true },
            { key: 'date_premiere_convention', label: 'Date de première convention', type: 'date', required: true },
            { key: 'date_derniere_convention', label: 'Date de dernière convention', type: 'date', required: true },
            { key: 'mois_depuis_derniere_convention', label: 'Mois depuis la dernière convention', type: 'readonly' },
            { key: 'lieu_vie_type', label: 'Situation résidentielle', type: 'combobox', required: true },
            { key: 'bailleur', label: 'Bailleur', type: 'combobox' },
            { key: 'numero_suivi', label: 'N° de suivi', type: 'number' },
            { key: 'nombre_logements_depuis_debut_suivi', label: 'Nombre de logements depuis début du suivi', type: 'number' },
            { key: 'wrap', label: 'Wrap', type: 'enum', options: OUI_NON_EN_COURS },
            { key: 'farde_locataire', label: 'Farde locataire', type: 'enum', options: OUI_NON_EN_COURS },
            { key: 'carnet_morts_rue', label: 'Carnet des Morts de la rue', type: 'enum', options: OUI_NON_EN_COURS },
        ],
    },
    {
        title: 'Revenu',
        headerBg: 'bg-sky-50',
        headerText: 'text-sky-800',
        cardBorder: 'border-sky-200',
        fields: [
            { key: 'organisme_paiement', label: 'Type de revenu', type: 'combobox', required: true },
            { key: 'type_revenu', label: 'Organisme', type: 'text' },
            { key: 'montant_revenu', label: 'Montant du revenu', type: 'text' },
            { key: 'frequence_revenu', label: 'Fréquence du revenu', type: 'text' },
            { key: 'as_referente_cpas', label: 'AS référent.e si CPAS', type: 'text' },
            { key: 'telephone_as_referente', label: 'Téléphone AS référent.e', type: 'text' },
        ],
    },
    {
        title: 'Banque',
        headerBg: 'bg-purple-50',
        headerText: 'text-purple-800',
        cardBorder: 'border-purple-200',
        fields: [
            { key: 'banque_nom', label: 'Nom de la banque', type: 'text' },
            { key: 'banque_numero_compte', label: 'N° de compte', type: 'text' },
            { key: 'banque_numero_carte', label: 'N° de carte bancaire', type: 'text' },
            { key: 'banque_code_pin', label: 'Code PIN', type: 'text' },
            { key: 'banque_numero_dossier', label: 'N° de dossier', type: 'text' },
        ],
    },
    {
        title: 'Mutuelle',
        headerBg: 'bg-indigo-50',
        headerText: 'text-indigo-800',
        cardBorder: 'border-indigo-200',
        fields: [
            { key: 'mutuelle_nom', label: 'Nom de la mutuelle', type: 'text' },
            { key: 'assurabilite', label: 'Assurabilité', type: 'enum', options: OK_PAS_OK },
            { key: 'assurabilite_date_fin', label: "Date de fin d'assurabilité", type: 'date' },
            { key: 'assurabilite_numero', label: "N° d'assurabilité", type: 'text' },
            { key: 'bim', label: 'BIM', type: 'enum', options: OK_PAS_OK },
            { key: 'bim_date_fin', label: 'Date de fin BIM', type: 'date' },
            { key: 'bim_code_vignette', label: 'Code de la vignette', type: 'text' },
            { key: 'assurance_complementaire', label: 'Assurance complémentaire', type: 'enum', options: OK_PAS_OK },
            { key: 'assurance_complementaire_cotisations', label: 'Cotisations', type: 'text' },
            { key: 'assurance_complementaire_specificite', label: 'Spécificité', type: 'text' },
        ],
    },
    {
        title: 'Informations médicales',
        headerBg: 'bg-emerald-50',
        headerText: 'text-emerald-800',
        cardBorder: 'border-emerald-200',
        fields: [
            { key: 'carte_medicale', label: 'Carte médicale', type: 'text' },
            { key: 'maison_medicale', label: 'Maison médicale', type: 'text' },
            { key: 'medecin_referent', label: 'Médecin référent', type: 'text' },
            { key: 'pharmacie', label: 'Pharmacie', type: 'text' },
            { key: 'informations_medicales', label: 'Informations médicales', type: 'textarea' },
        ],
    },
    {
        title: 'Justice',
        headerBg: 'bg-pink-50',
        headerText: 'text-pink-800',
        cardBorder: 'border-pink-200',
        fields: [
            { key: 'justice_probleme_en_cours', label: 'Problème en cours', type: 'enum', options: OUI_NON },
            { key: 'justice_type_probleme', label: 'Type de problème', type: 'text' },
            { key: 'justice_as', label: 'AS de justice', type: 'enum', options: OUI_NON },
            { key: 'justice_nom', label: 'Nom', type: 'text' },
            { key: 'justice_telephone', label: 'Numéro de téléphone', type: 'text' },
            { key: 'justice_email', label: 'Adresse e-mail', type: 'text' },
            { key: 'justice_avocat', label: 'Avocat', type: 'text' },
            { key: 'justice_avocat_telephone', label: 'Téléphone de contact', type: 'text' },
            { key: 'justice_avocat_mail', label: 'Mail de contact', type: 'text' },
        ],
    },
    {
        title: 'Administration',
        headerBg: 'bg-cyan-50',
        headerText: 'text-cyan-800',
        cardBorder: 'border-cyan-200',
        fields: [
            { key: 'administration_type', label: "Type d'administration", type: 'enum', options: ADMIN_TYPE },
            { key: 'administration_nom', label: 'Nom', type: 'text' },
            { key: 'administration_adresse', label: 'Adresse', type: 'text' },
            { key: 'administration_telephone', label: 'Numéro de téléphone', type: 'text' },
            { key: 'administration_email', label: 'Adresse e-mail', type: 'text' },
        ],
    },
    {
        title: 'Dettes',
        headerBg: 'bg-amber-50',
        headerText: 'text-amber-800',
        cardBorder: 'border-amber-200',
        fields: [
            { key: 'dettes_en_cours', label: 'Dettes en cours', type: 'enum', options: OUI_NON },
            { key: 'dettes_type', label: 'Type de dettes', type: 'text' },
            { key: 'dettes_mediateur', label: 'Médiateur de dettes', type: 'text' },
            { key: 'dettes_nom', label: 'Nom', type: 'text' },
            { key: 'dettes_telephone', label: 'Numéro de téléphone', type: 'text' },
            { key: 'dettes_email', label: 'Adresse e-mail', type: 'text' },
            { key: 'dettes_solution', label: 'Solution mise en place', type: 'text' },
            { key: 'dettes_type_solution', label: 'Type de solution', type: 'text' },
        ],
    },
    {
        title: 'Lieu de vie',
        headerBg: 'bg-orange-50',
        headerText: 'text-orange-800',
        cardBorder: 'border-orange-200',
        fields: [
            { key: 'lieu_vie_type', label: 'Type de lieu de vie', type: 'combobox', required: true },
            { key: 'lieu_vie_date_debut', label: 'Date de début', type: 'date' },
            { key: 'lieu_vie_remarque', label: 'Remarque', type: 'text' },
            { key: 'lieu_vie_rue', label: 'Rue', type: 'text' },
            { key: 'lieu_vie_numero_boite', label: 'Numéro de boîte', type: 'text' },
            { key: 'lieu_vie_commune', label: 'Commune', type: 'text' },
            { key: 'lieu_vie_personne_contact', label: 'Personne de contact', type: 'text' },
            { key: 'lieu_vie_telephone_contact', label: 'Téléphone de contact', type: 'text' },
        ],
    },
    {
        title: 'Logement',
        headerBg: 'bg-violet-50',
        headerText: 'text-violet-800',
        cardBorder: 'border-violet-200',
        fields: [
            { key: 'logement_type', label: 'Type de logement', type: 'combobox' },
            { key: 'logement_rue', label: 'Rue', type: 'text' },
            { key: 'logement_numero_boite', label: 'Numéro de boîte', type: 'text' },
            { key: 'logement_etage', label: 'Étage', type: 'text' },
            { key: 'logement_numero_appartement', label: "Numéro d'appartement", type: 'text' },
            { key: 'logement_commune', label: 'Commune', type: 'text' },
            { key: 'logement_partenaire', label: 'Partenaire (logement)', type: 'enum', options: BAILLEUR },
            { key: 'logement_type_bail_convention', label: 'Type de bail / convention', type: 'text' },
            { key: 'logement_montant_loyer', label: 'Montant du loyer', type: 'text' },
            { key: 'logement_montant_charges_communes', label: 'Montant des charges communes', type: 'text' },
            { key: 'logement_montant_total_loyer', label: 'Montant total du loyer', type: 'text' },
            { key: 'logement_date_signature_bail', label: 'Date de signature de bail', type: 'date' },
            { key: 'logement_date_fin_bail', label: 'Date de fin de bail', type: 'date' },
            { key: 'logement_type_gl', label: 'Type de GL', type: 'enum', options: GL_TYPE },
            { key: 'logement_remboursement_gl', label: 'Remboursement de GL', type: 'enum', options: GL_REMBOURSEMENT },
            { key: 'autorisation_cles', label: 'Autorisation clés', type: 'enum', options: OUI_NON },
            { key: 'cles_au_bureau', label: 'Clés au bureau', type: 'boolean' },
        ],
    },
    {
        title: 'Historique Logement',
        headerBg: 'bg-violet-50',
        headerText: 'text-violet-800',
        cardBorder: 'border-violet-200',
        fields: [
            { key: 'historique_logement_1_adresse', label: 'Adresse 1', type: 'text' },
            { key: 'historique_logement_1_date_signature_bail', label: 'Bail 1 - Date de signature', type: 'date' },
            { key: 'historique_logement_1_date_renom', label: 'Bail 1 - Date du renom', type: 'date' },
            { key: 'historique_logement_1_raison_invoquee', label: 'Bail 1 - Raison invoquée', type: 'enum', options: RAISON_RENOM },
            { key: 'historique_logement_2_adresse', label: 'Adresse 2', type: 'text' },
            { key: 'historique_logement_2_date_signature_bail', label: 'Bail 2 - Date de signature', type: 'date' },
            { key: 'historique_logement_2_date_renom', label: 'Bail 2 - Date du renom', type: 'date' },
            { key: 'historique_logement_2_raison_invoquee', label: 'Bail 2 - Raison invoquée', type: 'enum', options: RAISON_RENOM },
            { key: 'historique_logement_3_adresse', label: 'Adresse 3', type: 'text' },
            { key: 'historique_logement_3_date_signature_bail', label: 'Bail 3 - Date de signature', type: 'date' },
            { key: 'historique_logement_3_date_renom', label: 'Bail 3 - Date du renom', type: 'date' },
            { key: 'historique_logement_3_raison_invoquee', label: 'Bail 3 - Raison invoquée', type: 'enum', options: RAISON_RENOM },
        ],
    },
    {
        title: 'Eau',
        headerBg: 'bg-teal-50',
        headerText: 'text-teal-800',
        cardBorder: 'border-teal-200',
        fields: [
            { key: 'eau_organisme', label: 'Organisme', type: 'text' },
            { key: 'eau_numero_dossier', label: 'Numéro de dossier', type: 'text' },
            { key: 'eau_personne_contact', label: 'Personne de contact', type: 'text' },
            { key: 'eau_montant', label: 'Montant', type: 'text' },
            { key: 'eau_statut_paiements', label: 'Statut des paiements', type: 'text' },
            { key: 'eau_specificite', label: 'Spécificité', type: 'text' },
        ],
    },
    {
        title: 'Électricité / Gaz',
        headerBg: 'bg-teal-50',
        headerText: 'text-teal-800',
        cardBorder: 'border-teal-200',
        fields: [
            { key: 'electricite_gaz_organisme', label: 'Organisme', type: 'text' },
            { key: 'electricite_gaz_numero_dossier', label: 'Numéro de dossier', type: 'text' },
            { key: 'electricite_gaz_personne_contact', label: 'Personne de contact', type: 'text' },
            { key: 'electricite_gaz_montant', label: 'Montant', type: 'text' },
            { key: 'electricite_gaz_statut_paiements', label: 'Statut des paiements', type: 'text' },
            { key: 'electricite_gaz_specificite', label: 'Spécificité', type: 'text' },
        ],
    },
    {
        title: 'Internet / Télévision',
        headerBg: 'bg-teal-50',
        headerText: 'text-teal-800',
        cardBorder: 'border-teal-200',
        fields: [
            { key: 'internet_television_organisme', label: 'Organisme', type: 'text' },
            { key: 'internet_television_numero_dossier', label: 'Numéro de dossier', type: 'text' },
            { key: 'internet_television_personne_contact', label: 'Personne de contact', type: 'text' },
            { key: 'internet_television_montant', label: 'Montant', type: 'text' },
            { key: 'internet_television_statut_paiements', label: 'Statut des paiements', type: 'text' },
            { key: 'internet_television_specificite', label: 'Spécificité', type: 'text' },
        ],
    },
    {
        title: 'Suspension de suivi',
        headerBg: 'bg-rose-50',
        headerText: 'text-rose-800',
        cardBorder: 'border-rose-200',
        fields: [
            { key: 'date_suspension_termination', label: 'Date de suspension de suivi', type: 'date' },
            { key: 'cause_suspension_termination', label: 'Raison de suspension de suivi', type: 'combobox' },
        ],
    },
]

const CRITERES = [
    {
        key: 'critere_sans_abri_ok',
        commentKey: 'critere_sans_abri_commentaire',
        label: 'Sans-abri',
        description: "Personne vivant dans la rue ou dans des hébergements d'urgence (ETHOS 1 et 2).",
    },
    {
        key: 'critere_de_longue_duree_ok',
        commentKey: 'critere_de_longue_duree_commentaire',
        label: 'De longue durée',
        description: "Minimum 3 mois continus ou 12 mois cumulés avant l'entrée en logement.",
    },
    {
        key: 'critere_fragile_ok',
        commentKey: 'critere_fragile_commentaire',
        label: 'Fragile',
        description: 'Problématiques de santé physique, mentale et/ou de toxicomanie.',
    },
    {
        key: 'critere_accompagnement_intensif_ok',
        commentKey: 'critere_accompagnement_intensif_commentaire',
        label: 'Accompagnement intensif',
        description: 'Nécessitant un accompagnement intensif en logement.',
    },
    {
        key: 'critere_source_revenus_ok',
        commentKey: 'critere_source_revenus_commentaire',
        label: 'Source de revenus',
        description: "Pouvant ouvrir le droit à un revenu ou disposant déjà d'un revenu.",
    },
]

// ── Field row ──────────────────────────────────────────────────────────────
interface FieldRowProps {
    config: FieldConfig
    value: unknown
    editingFields: Record<string, string>
    copiedField: string | null
    saving: boolean
    onEdit: (key: string, current: unknown) => void
    onSave: (key: string) => void
    onCancel: (key: string) => void
    onCopy: (key: string, value: unknown) => void
    onEditValueChange: (key: string, v: string) => void
    workers?: { id: string; name: string }[]
    colorOverride?: string
    isBadge?: boolean
    isAdmin?: boolean
}

const FieldRow = ({
    config,
    value,
    editingFields,
    copiedField,
    saving,
    onEdit,
    onSave,
    onCancel,
    onCopy,
    onEditValueChange,
    workers = [],
    colorOverride,
    isBadge,
    isAdmin = false,
}: FieldRowProps) => {
    const isEditing = config.key in editingFields
    const editingValue = editingFields[config.key] ?? ''
    const isCopied = copiedField === config.key
    const isEmpty = value == null || value === ''
    const isRequiredMissing = config.required && isEmpty

    const displayValue = (): string | null => {
        if (config.type === 'boolean') {
            if (value === true) return 'Oui'
            if (value === false) return 'Non'
            return null
        }
        if (isEmpty) return null
        if (config.type === 'worker') {
            return workers.find(w => w.id === value)?.name ?? null
        }
        if (config.type === 'date') {
            try {
                // Parse ISO date as local to avoid UTC offset shifting the day
                const [y, m, d] = String(value).split('-').map(Number)
                const date = new Date(y, m - 1, d)
                return date.toLocaleDateString('fr-FR', {
                    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
                })
            } catch { return String(value) }
        }
        return String(value)
    }

    const display = displayValue()

    return (
        <div className={`flex items-start gap-2 py-2.5 border-b border-gray-100 last:border-0 group
            ${isRequiredMissing ? 'bg-red-50 -mx-4 px-4 rounded' : ''}`}
        >
            <div className={`w-48 shrink-0 text-sm pt-0.5 leading-snug flex items-center gap-1
                ${isRequiredMissing ? 'text-red-600 font-medium' : 'text-gray-500'}`}
            >
                <span>{config.label}</span>
                {isRequiredMissing && <span className="text-red-400">*</span>}
            </div>
            <div className="flex-1 min-w-0">
                {isEditing ? (
                    <div className="flex items-start gap-2">
                        {config.type === 'worker' ? (
                            <select
                                value={editingValue}
                                onChange={e => onEditValueChange(config.key, e.target.value)}
                                autoFocus
                                className="flex-1 border border-blue-400 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                <option value="">—</option>
                                {workers.filter(w => w.is_referent).map(w => (
                                    <option key={w.id} value={w.id}>{w.name}</option>
                                ))}
                            </select>
                        ) : config.type === 'combobox' ? (
                            <SmartCombobox
                                column={config.key}
                                value={editingValue}
                                onChange={v => onEditValueChange(config.key, v)}
                                isAdmin={isAdmin}
                                autoFocus
                                inputClassName="flex-1 border border-blue-400 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        ) : config.type === 'enum' || config.type === 'boolean' ? (
                            <select
                                value={editingValue}
                                onChange={e => onEditValueChange(config.key, e.target.value)}
                                autoFocus
                                className="flex-1 border border-blue-400 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                <option value="">—</option>
                                {(config.type === 'boolean' ? ['Oui', 'Non'] : config.options ?? []).map(opt => (
                                    <option key={opt} value={opt}>{opt}</option>
                                ))}
                            </select>
                        ) : config.type === 'textarea' ? (
                            <textarea
                                value={editingValue}
                                onChange={e => onEditValueChange(config.key, e.target.value)}
                                autoFocus
                                rows={3}
                                className="flex-1 border border-blue-400 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y"
                            />
                        ) : (
                            <input
                                type={config.type === 'number' ? 'number' : config.type === 'date' ? 'date' : 'text'}
                                value={editingValue}
                                onChange={e => onEditValueChange(config.key, e.target.value)}
                                autoFocus
                                className="flex-1 border border-blue-400 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        )}
                        <button
                            onClick={() => onSave(config.key)}
                            disabled={saving}
                            title="Sauvegarder"
                            className="p-1.5 rounded bg-green-100 hover:bg-green-200 text-green-700 disabled:opacity-50 shrink-0 mt-0.5"
                        >
                            <Check className="h-3.5 w-3.5" />
                        </button>
                        <button
                            onClick={() => onCancel(config.key)}
                            disabled={saving}
                            title="Annuler"
                            className="p-1.5 rounded bg-gray-100 hover:bg-gray-200 text-gray-600 disabled:opacity-50 shrink-0 mt-0.5"
                        >
                            <X className="h-3.5 w-3.5" />
                        </button>
                    </div>
                ) : (
                    <div className="flex items-center gap-1 min-w-0">
                        {colorOverride && isBadge && display ? (
                            <span className={`inline-block px-1.5 py-0.5 rounded text-xs font-semibold ${colorOverride}`}>
                                {display}
                            </span>
                        ) : (
                        <span className={`text-sm flex-1 break-words
                            ${isRequiredMissing
                                ? 'text-red-400 italic'
                                : colorOverride && display
                                ? colorOverride
                                : display ? 'text-gray-900' : 'text-gray-300 italic'
                            }`}
                        >
                            {display ?? (isRequiredMissing ? 'Champ obligatoire manquant' : '—')}
                        </span>
                        )}
                        {config.type !== 'readonly' && (
                            <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity shrink-0 ml-1">
                                <button
                                    onClick={() => onCopy(config.key, value)}
                                    title="Copier"
                                    className={`p-1 rounded transition-colors ${isCopied ? 'text-green-600' : 'text-gray-400 hover:text-gray-700'}`}
                                >
                                    {isCopied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                                </button>
                                {!IS_DEMO_MODE && (
                                    <button
                                        onClick={() => onEdit(config.key, value)}
                                        title="Modifier"
                                        className="p-1 rounded text-gray-400 hover:text-blue-500 transition-colors"
                                    >
                                        <Pencil className="h-3.5 w-3.5" />
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    )
}

// ── Section wrapper ────────────────────────────────────────────────────────
const Section = ({
    title,
    headerBg,
    headerText,
    cardBorder,
    children,
}: {
    title: string
    headerBg: string
    headerText: string
    cardBorder: string
    children: React.ReactNode
}) => (
    <div className={`bg-white rounded-lg border overflow-hidden ${cardBorder}`}>
        <div className={`px-4 py-2.5 border-b ${headerBg} ${cardBorder}`}>
            <h2 className={`font-semibold text-xs uppercase tracking-wider ${headerText}`}>{title}</h2>
        </div>
        <div className="px-4">{children}</div>
    </div>
)

// ── Main component ─────────────────────────────────────────────────────────
interface BeneficiaireDetailProps {
    beneficiaireId: string
    onBack: () => void
}

export const BeneficiaireDetail = ({ beneficiaireId, onBack }: BeneficiaireDetailProps) => {
    const { workers } = useAppData()
    const { userProfile } = useAuth()
    const isAdmin = userProfile?.is_admin === true
    const [beneficiaire, setBeneficiaire] = useState<FullBeneficiary | null>(null)
    const [loading, setLoading] = useState(true)
    const [editingFields, setEditingFields] = useState<Record<string, string>>({})
    const [originalFields, setOriginalFields] = useState<Record<string, unknown>>({})
    const [pendingAction, setPendingAction] = useState<'save_all' | 'back' | null>(null)
    const [saving, setSaving] = useState(false)
    const [copiedField, setCopiedField] = useState<string | null>(null)
    const [saveError, setSaveError] = useState<string | null>(null)
    // Critère inline editing
    const [editingCritere, setEditingCritere] = useState<string | null>(null)
    const [editingCritereValue, setEditingCritereValue] = useState<boolean | null>(null)
    const [editingCritereComment, setEditingCritereComment] = useState('')
    const [savingCritere, setSavingCritere] = useState(false)

    useEffect(() => {
        const load = async () => {
            const { data } = await supabase
                .from('beneficiaries')
                .select('*')
                .eq('id', beneficiaireId)
                .single()
            setBeneficiaire(data as any)
            setLoading(false)
        }
        load()
    }, [beneficiaireId])

    const allFields = SECTIONS.flatMap(s => s.fields)

    const handleEdit = (key: string, current: unknown) => {
        setSaveError(null)
        const fieldConfig = allFields.find(f => f.key === key)
        const strValue = fieldConfig?.type === 'boolean'
            ? (current === true ? 'Oui' : current === false ? 'Non' : '')
            : fieldConfig?.type === 'date' && current
                ? String(current).slice(0, 10)
                : current != null ? String(current) : ''
        setEditingFields(prev => ({ ...prev, [key]: strValue }))
        setOriginalFields(prev => ({ ...prev, [key]: current }))
    }

    const handleCancel = (key: string) => {
        setEditingFields(prev => { const n = { ...prev }; delete n[key]; return n })
        setOriginalFields(prev => { const n = { ...prev }; delete n[key]; return n })
        setSaveError(null)
    }

    const handleSave = async (key: string) => {
        if (!beneficiaire) return
        setSaving(true)
        setSaveError(null)
        const fieldConfig = allFields.find(f => f.key === key)
        const rawValue = editingFields[key] ?? ''
        let parsed: unknown = rawValue || null
        if (fieldConfig?.type === 'number') parsed = rawValue !== '' ? parseFloat(rawValue) : null
        if (fieldConfig?.type === 'boolean') parsed = rawValue === 'Oui' ? true : rawValue === 'Non' ? false : null
        const { error } = await supabase
            .from('beneficiaries')
            .update({ [key]: parsed, updated_at: new Date().toISOString() })
            .eq('id', beneficiaire.id)
        if (error) {
            setSaveError('Erreur lors de la sauvegarde.')
        } else {
            setBeneficiaire(prev => prev ? { ...prev, [key]: parsed } : prev)
            setEditingFields(prev => { const n = { ...prev }; delete n[key]; return n })
            setOriginalFields(prev => { const n = { ...prev }; delete n[key]; return n })
            if (fieldConfig?.type === 'combobox') invalidateDistinctCache(key)
        }
        setSaving(false)
    }

    const handleBack = () => {
        const hasDirty = Object.keys(editingFields).some(key => {
            const original = originalFields[key]
            return (editingFields[key] ?? '') !== (original != null ? String(original) : '')
        })
        if (hasDirty) setPendingAction('back')
        else onBack()
    }

    const handleDiscardAll = () => {
        setEditingFields({})
        setOriginalFields({})
        const wasBack = pendingAction === 'back'
        setPendingAction(null)
        if (wasBack) onBack()
    }

    const handleConfirmSaveAll = async () => {
        if (!beneficiaire) return
        setSaving(true)
        setSaveError(null)
        const updates: Record<string, unknown> = {}
        for (const [key, rawValue] of Object.entries(editingFields)) {
            const fieldConfig = allFields.find(f => f.key === key)
            let parsed: unknown = rawValue || null
            if (fieldConfig?.type === 'number') parsed = rawValue !== '' ? parseFloat(rawValue) : null
            if (fieldConfig?.type === 'boolean') parsed = rawValue === 'Oui' ? true : rawValue === 'Non' ? false : null
            updates[key] = parsed
        }
        updates.updated_at = new Date().toISOString()
        const { error } = await supabase
            .from('beneficiaries')
            .update(updates)
            .eq('id', beneficiaire.id)
        if (error) {
            setSaveError('Erreur lors de la sauvegarde.')
            setPendingAction(null)
            setSaving(false)
            return
        }
        setBeneficiaire(prev => prev ? { ...prev, ...updates } : prev)
        setEditingFields({})
        setOriginalFields({})
        const wasBack = pendingAction === 'back'
        setPendingAction(null)
        setSaving(false)
        if (wasBack) onBack()
    }

    const handleCopy = (key: string, value: unknown) => {
        if (value == null || value === '') return
        navigator.clipboard.writeText(String(value)).catch(() => {})
        setCopiedField(key)
        setTimeout(() => setCopiedField(f => f === key ? null : f), 1500)
    }

    const handleCritereOpen = (key: string, commentKey: string) => {
        if (!beneficiaire) return
        setEditingCritere(key)
        setEditingCritereValue(beneficiaire[key] ?? null)
        setEditingCritereComment(beneficiaire[commentKey] ?? '')
    }

    const handleCritereCancel = () => {
        setEditingCritere(null)
        setEditingCritereValue(null)
        setEditingCritereComment('')
    }

    const handleCritereSave = async (key: string, commentKey: string) => {
        if (!beneficiaire) return
        setSavingCritere(true)
        const { error } = await supabase
            .from('beneficiaries')
            .update({
                [key]: editingCritereValue,
                [commentKey]: editingCritereComment || null,
                updated_at: new Date().toISOString(),
            })
            .eq('id', beneficiaire.id)
        if (!error) {
            setBeneficiaire(prev => prev ? {
                ...prev,
                [key]: editingCritereValue,
                [commentKey]: editingCritereComment || null,
            } : prev)
            setEditingCritere(null)
        }
        setSavingCritere(false)
    }

    const fullName = beneficiaire
        ? (() => {
            const full = [beneficiaire.prenom, beneficiaire.nom].filter(Boolean).join(' ') || 'Locataire'
            return beneficiaire.numero_suivi != null ? `${full} (${beneficiaire.numero_suivi})` : full
        })()
        : 'Locataire'

    const dirtyFields = Object.entries(editingFields).filter(([key, draft]) => {
        const original = originalFields[key]
        return draft !== (original != null ? String(original) : '')
    })

    const hasDirty = dirtyFields.length > 0

    const rowProps = {
        editingFields,
        copiedField,
        saving,
        workers,
        isAdmin,
        onEdit: handleEdit,
        onSave: handleSave,
        onCancel: handleCancel,
        onCopy: handleCopy,
        onEditValueChange: (key: string, v: string) => setEditingFields(prev => ({ ...prev, [key]: v })),
    }

    // Check if critères section has missing values
    const criteresMissing = beneficiaire
        ? CRITERES.some(c => beneficiaire[c.key] == null)
        : false

    return (
        <div className="min-h-screen bg-gray-50">
            <PageHeader
                title={fullName}
                icon={<User className="h-5 w-5" />}
                onBack={handleBack}
            />

            {beneficiaire && (() => {
                const tags: string[] = beneficiaire.tags ?? []
                const toggleTag = async (tag: string) => {
                    if (IS_DEMO_MODE) return
                    const newTags = computeNewTags(tags, tag)
                    setBeneficiaire(prev => prev ? { ...prev, tags: newTags } : prev)
                    await supabase.from('beneficiaries').update({ tags: newTags }).eq('id', beneficiaire.id)
                }
                return (
                    <div className="max-w-3xl mx-auto px-4 pt-4 flex items-center gap-2">
                        {(!IS_DEMO_MODE || tags.includes('binome')) && (
                        <button
                            type="button"
                            onClick={() => toggleTag('binome')}
                            className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border transition-colors ${tags.includes('binome')
                                ? 'bg-orange-100 text-orange-700 border-orange-200 hover:bg-orange-200'
                                : 'bg-white text-gray-400 border-gray-200 hover:bg-gray-50'
                                }`}
                        >
                            <UsersRound size={12} />
                            {tags.includes('binome') ? 'binôme' : '+ binôme'}
                        </button>
                        )}
                        {(!IS_DEMO_MODE || tags.includes('solo')) && (
                        <button
                            type="button"
                            onClick={() => toggleTag('solo')}
                            className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border transition-colors ${tags.includes('solo')
                                ? 'bg-purple-100 text-purple-700 border-purple-200 hover:bg-purple-200'
                                : 'bg-white text-gray-400 border-gray-200 hover:bg-gray-50'
                                }`}
                        >
                            {tags.includes('solo') ? 'solo' : '+ solo'}
                        </button>
                        )}
                    </div>
                )
            })()}

            {loading ? (
                <div className="flex justify-center pt-16">
                    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
                </div>
            ) : !beneficiaire ? (
                <div className="max-w-3xl mx-auto px-4 py-12 text-center text-gray-500">
                    Locataire introuvable.
                </div>
            ) : (
                <div className="max-w-3xl mx-auto px-4 py-6 space-y-4 pb-16">
                    {saveError && (
                        <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-2 rounded-lg">
                            {saveError}
                        </div>
                    )}

                    {hasDirty && (
                        <div className="sticky top-14 z-20 flex justify-end">
                            <button
                                onClick={() => setPendingAction('save_all')}
                                className="px-4 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium shadow"
                            >
                                Tout sauvegarder ({dirtyFields.length})
                            </button>
                        </div>
                    )}

                    {/* Regular sections */}
                    {SECTIONS.map(section => (
                        <Section
                            key={section.title}
                            title={section.title}
                            headerBg={section.headerBg}
                            headerText={section.headerText}
                            cardBorder={section.cardBorder}
                        >
                            {section.fields.map(field => {
                                const months = computeMonthsSince(beneficiaire.date_derniere_convention)
                                const color = convColor(months)
                                const colorMap = { red: 'text-red-600 font-semibold', orange: 'text-orange-500 font-semibold', green: 'text-green-700' }
                                const badgeMap = { red: 'bg-red-100 text-red-700', orange: 'bg-orange-100 text-orange-700', green: 'bg-green-100 text-green-700' }
                                const fieldValue =
                                    field.key === 'mois_depuis_derniere_convention' ? months
                                    : field.key === 'age' ? computeAge(beneficiaire.date_naissance)
                                    : beneficiaire[field.key]
                                const colorOverride =
                                    field.key === 'mois_depuis_derniere_convention' ? (color ? badgeMap[color] : undefined)
                                    : field.key === 'date_derniere_convention' ? (color ? colorMap[color] : undefined)
                                    : undefined
                                const isBadge = field.key === 'mois_depuis_derniere_convention'
                                return (
                                    <FieldRow
                                        key={field.key}
                                        config={field}
                                        value={fieldValue}
                                        colorOverride={colorOverride}
                                        isBadge={isBadge}
                                        {...rowProps}
                                    />
                                )
                            })}
                            {section.title === 'Suspension de suivi' && (
                                <p className="text-[11px] text-rose-400 italic py-2.5 border-t border-rose-100 mt-1">
                                    * Pour rendre un locataire inactif, un admin doit aller dans <strong>Gestion d'équipe</strong> (réglage en haut à droite) → Locataires → icône édition.
                                </p>
                            )}
                        </Section>
                    ))}

                    {/* Critères Housing First */}
                    <div className={`bg-white rounded-lg border overflow-hidden border-purple-200`}>
                        <div className={`px-4 py-2.5 border-b bg-purple-50 border-purple-200 flex items-center gap-2`}>
                            <h2 className="font-semibold text-xs uppercase tracking-wider text-purple-800">
                                Critères Housing First - Tableau récapitulatif
                            </h2>
                            {criteresMissing && (
                                <span className="text-xs text-red-500 font-medium">● champs manquants</span>
                            )}
                        </div>
                        <div className="px-4">
                            {CRITERES.map(c => {
                                const val = beneficiaire[c.key]
                                const comment = beneficiaire[c.commentKey] ?? ''
                                const isMissing = val == null
                                const isEditingThis = editingCritere === c.key

                                return (
                                    <div
                                        key={c.key}
                                        className={`py-3 border-b border-gray-100 last:border-0 group
                                            ${isMissing && !isEditingThis ? 'bg-red-50 -mx-4 px-4 rounded' : ''}`}
                                    >
                                        {isEditingThis ? (
                                            /* ── Edit mode ── */
                                            <div className="space-y-3">
                                                <p className="text-sm font-medium text-gray-800">{c.label}</p>
                                                {/* Oui / Non / ? toggle */}
                                                <div className="flex gap-2">
                                                    {([true, false, null] as (boolean | null)[]).map(opt => {
                                                        const isSelected = editingCritereValue === opt
                                                        const label = opt === true ? 'Oui' : opt === false ? 'Non' : '?'
                                                        const base = 'px-3 py-1 rounded-full text-sm font-medium border transition-colors'
                                                        const active =
                                                            opt === true ? 'bg-green-100 border-green-400 text-green-800' :
                                                            opt === false ? 'bg-red-100 border-red-400 text-red-700' :
                                                            'bg-gray-100 border-gray-400 text-gray-600'
                                                        const inactive = 'bg-white border-gray-200 text-gray-400 hover:border-gray-400'
                                                        return (
                                                            <button
                                                                key={String(opt)}
                                                                onClick={() => setEditingCritereValue(opt)}
                                                                className={`${base} ${isSelected ? active : inactive}`}
                                                            >
                                                                {label}
                                                            </button>
                                                        )
                                                    })}
                                                </div>
                                                {/* Comment */}
                                                <textarea
                                                    value={editingCritereComment}
                                                    onChange={e => setEditingCritereComment(e.target.value)}
                                                    placeholder="Commentaire (optionnel)..."
                                                    rows={2}
                                                    className="w-full border border-blue-400 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y"
                                                />
                                                {/* Description (read-only) */}
                                                <p className="text-xs text-gray-400 italic">{c.description}</p>
                                                {/* Save / Cancel */}
                                                <div className="flex gap-2">
                                                    <button
                                                        onClick={() => handleCritereSave(c.key, c.commentKey)}
                                                        disabled={savingCritere}
                                                        className="flex items-center gap-1 px-3 py-1 rounded bg-green-100 hover:bg-green-200 text-green-700 text-sm disabled:opacity-50"
                                                    >
                                                        <Check className="h-3.5 w-3.5" /> Sauvegarder
                                                    </button>
                                                    <button
                                                        onClick={handleCritereCancel}
                                                        disabled={savingCritere}
                                                        className="flex items-center gap-1 px-3 py-1 rounded bg-gray-100 hover:bg-gray-200 text-gray-600 text-sm disabled:opacity-50"
                                                    >
                                                        <X className="h-3.5 w-3.5" /> Annuler
                                                    </button>
                                                </div>
                                            </div>
                                        ) : (
                                            /* ── Display mode ── */
                                            <div className="flex items-start gap-3">
                                                <div
                                                    className={`mt-0.5 w-6 h-6 rounded-full flex items-center justify-center shrink-0 text-xs font-bold
                                                        ${val === true
                                                            ? 'bg-green-100 text-green-700'
                                                            : isMissing
                                                                ? 'bg-red-100 text-red-400'
                                                                : 'bg-gray-100 text-gray-400'
                                                        }`}
                                                >
                                                    {val === true ? '✓' : isMissing ? '?' : '✗'}
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <p className={`text-sm font-medium ${isMissing ? 'text-red-700' : 'text-gray-800'}`}>
                                                        {c.label}
                                                        {isMissing && <span className="ml-1 text-red-400 text-xs">*</span>}
                                                    </p>
                                                    {comment && (
                                                        <p className="text-sm text-gray-600 mt-0.5">{comment}</p>
                                                    )}
                                                    <p className="text-xs text-gray-400 italic mt-0.5">{c.description}</p>
                                                </div>
                                                {!IS_DEMO_MODE && (
                                                    <button
                                                        onClick={() => handleCritereOpen(c.key, c.commentKey)}
                                                        title="Modifier"
                                                        className="opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded text-gray-400 hover:text-blue-500 shrink-0 mt-0.5"
                                                    >
                                                        <Pencil className="h-3.5 w-3.5" />
                                                    </button>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )
                            })}
                        </div>
                    </div>
                    {hasDirty && (
                        <div className="flex justify-end">
                            <button
                                onClick={() => setPendingAction('save_all')}
                                className="px-4 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium shadow"
                            >
                                Tout sauvegarder ({Object.keys(editingFields).length})
                            </button>
                        </div>
                    )}
                </div>
            )}

            {/* Confirmation popup */}
            {pendingAction && (
                <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
                    <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 space-y-4">
                        <h3 className="font-semibold text-gray-900 text-base">
                            {dirtyFields.length > 0 ? 'Modifications en cours' : 'Aucune modification'}
                        </h3>

                        {dirtyFields.length > 0 ? (
                            <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                                {dirtyFields.map(([key, draft]) => {
                                    const fieldConfig = allFields.find(f => f.key === key)
                                    const label = fieldConfig?.label ?? key
                                    const original = originalFields[key]
                                    const oldStr = original != null && original !== '' ? String(original) : '—'
                                    const newStr = draft || '—'
                                    return (
                                        <div key={key} className="text-sm border rounded-lg px-3 py-2 bg-gray-50">
                                            <p className="font-medium text-gray-700 mb-1">{label}</p>
                                            <p className="text-gray-400 line-through text-xs">{oldStr}</p>
                                            <p className="text-green-700 text-xs font-medium">{newStr}</p>
                                        </div>
                                    )
                                })}
                            </div>
                        ) : (
                            <p className="text-sm text-gray-500">Aucun champ n'a été modifié.</p>
                        )}

                        <div className="flex flex-col gap-2 pt-1">
                            {dirtyFields.length > 0 && (
                                <button
                                    onClick={handleConfirmSaveAll}
                                    disabled={saving}
                                    className="w-full px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium disabled:opacity-50"
                                >
                                    Tout sauvegarder
                                </button>
                            )}
                            <button
                                onClick={handleDiscardAll}
                                className="w-full px-4 py-2 rounded-lg bg-orange-100 hover:bg-orange-200 text-orange-700 text-sm font-medium"
                            >
                                {pendingAction === 'back' ? 'Ignorer et retourner' : 'Ignorer les modifications'}
                            </button>
                            <button
                                onClick={() => setPendingAction(null)}
                                className="w-full px-4 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-600 text-sm"
                            >
                                Continuer l'édition
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}
